import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Landmark, Repeat, CreditCard, FileText, Receipt,
  Settings, Building2, LogOut, Sparkles, HandCoins, ArrowDownUp, Wallet, UserCog, Archive,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Wordmark } from "@/components/Wordmark";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationBell } from "@/components/NotificationBell";
import { FxWidget } from "@/components/FxWidget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/panel", icon: LayoutDashboard, label: "Panel", end: true, tid: "nav-panel" },
  { to: "/abonelikler", icon: Repeat, label: "Abonelikler", tid: "nav-abonelikler" },
  { to: "/cekler/alinan", icon: ArrowDownUp, label: "Alınan Çekler", tid: "nav-alinan-cekler" },
  { to: "/cekler/bankaya-verilen", icon: Landmark, label: "Bankaya Verilen Çekler", tid: "nav-bankaya-verilen" },
  { to: "/cekler/cirolanan", icon: ArrowDownUp, label: "Cirolanan Çekler", tid: "nav-cirolanan" },
  { to: "/cekler/kendi", icon: FileText, label: "Kendi Çeklerimiz", tid: "nav-kendi-ceklerimiz" },
  { to: "/krediler", icon: Landmark, label: "Krediler", tid: "nav-krediler" },
  { to: "/dbs", icon: Landmark, label: "DBS", tid: "nav-dbs" },
  { to: "/odemeler", icon: HandCoins, label: "Diğer Ödemeler", tid: "nav-diger-odemeler" },
  { to: "/tahsilatlar", icon: HandCoins, label: "Diğer Tahsilatlar", tid: "nav-diger-tahsilatlar" },
  { to: "/kasa", icon: Wallet, label: "Kasa", tid: "nav-kasa" },
  { to: "/kredi-kartlari", icon: CreditCard, label: "Kredi Kartları", tid: "nav-kredi-kartlari" },
  { to: "/pos-hareketleri", icon: Receipt, label: "POS Hareketleri", tid: "nav-pos-hareketleri" },
];

function NavItem({ to, icon: Icon, label, end, tid }) {
  const { t } = useLang();
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={tid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
          isActive
            ? "bg-blue-600 text-white"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {t(label)}
    </NavLink>
  );
}

const ROLE_LABELS = {
  super_admin: "Süper Admin",
  admin: "Admin",
  editor: "Yazabilir",
  viewer: "İzleyici",
};

function ConnectionStatus() {
  const { t } = useLang();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return (
    <span data-testid="network-status"
      className={`hidden md:inline-flex items-center gap-1.5 text-xs border rounded-md px-2.5 py-1.5 ${
        online ? "text-green-700 border-green-200 bg-green-50" : "text-red-700 border-red-200 bg-red-50"
      }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${online ? "bg-green-500" : "bg-red-500"}`} />
      {online ? t("Bağlı") : t("Çevrimdışı")}
    </span>
  );
}

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="dashboard-layout">
      <aside className="w-64 shrink-0 bg-[#0A1128] flex flex-col fixed inset-y-0 z-30">
        <div className="p-6 border-b border-white/10">
          <Wordmark dark />
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/kullanicilar"
              data-testid="nav-kullanicilar"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <UserCog className="h-4 w-4" />
              {t("Kullanıcılar")}
            </NavLink>
          )}
          {user?.role === "super_admin" && (
            <NavLink
              to="/admin/tenants"
              data-testid="nav-firmalar"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Building2 className="h-4 w-4" />
              {t("Firmalar")}
            </NavLink>
          )}
          <NavLink
            to="/yedekleme"
            data-testid="nav-yedekleme"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                isActive
                  ? "bg-blue-600 text-white"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Archive className="h-4 w-4" />
            {t("Yedekleme")}
          </NavLink>
          <NavLink
            to="/settings"
            data-testid="nav-ayarlar"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                isActive
                  ? "bg-blue-600 text-white"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Settings className="h-4 w-4" />
            {t("Ayarlar")}
          </NavLink>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="rounded-md bg-blue-600/10 border border-blue-500/20 p-3 mb-4" data-testid="subscription-teaser">
            <div className="flex items-center gap-2 text-blue-400 text-xs font-medium mb-1">
              <Sparkles className="h-3.5 w-3.5" />
              {t("Abonelik yakında")}
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              {t("Iyzico ile aylık/yıllık planlar çok yakında.")}
            </p>
          </div>
          <div className="mb-3">
            <LanguageToggle dark />
          </div>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
              {(user?.name || user?.email || "T").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.name || user?.email}</p>
              <Badge variant="outline" className="text-[10px] border-blue-500/40 text-blue-400 mt-0.5" data-testid="sidebar-user-role">
                {t(ROLE_LABELS[user?.role] || user?.role)}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              aria-label={t("Çıkış yap")}
              data-testid="logout-button"
              className="text-slate-400 hover:text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 ml-64 p-8 lg:p-12 pt-6">
        <div className="mb-8 flex items-center gap-3" data-testid="topbar">
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <FxWidget />
            <ConnectionStatus />
            <NotificationBell />
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
